/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
 the European Commission - subsequent versions of the EUPL (the "Licence");
 You may not use this work except in compliance with the Licence.
 You may obtain a copy of the Licence at:

   https://joinup.ec.europa.eu/software/page/eupl

 Unless required by applicable law or agreed to in writing, software
 distributed under the Licence is distributed on an "AS IS" basis,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the Licence for the specific language governing permissions and
 limitations under the Licence. */

import { useCallback, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch } from "react-redux";
import { UserActions } from "../../../../actions";
import { getGroupOfStopPlacesById } from "../../../../actions/TiamatActions.modern";
import Routes from "../../../../routes";
import { useNavigateToStopPlace } from "../useNavigateToStopPlace";
import { useConfirmLeave } from "../UnsavedChangesGuard";

/**
 * One navigation surface for both membership targets, so each layout variant
 * stays presentational. Parent navigation reuses the shared stop place hook;
 * group navigation mirrors it for the group route.
 */
export const useMembershipNavigation = () => {
  const dispatch = useDispatch() as any;
  const confirmLeave = useConfirmLeave();
  const {
    loading: parentLoading,
    loadingName: parentLoadingName,
    navigateTo: goToParent,
  } = useNavigateToStopPlace();

  const [groupLoading, setGroupLoading] = useState(false);
  const [groupLoadingName, setGroupLoadingName] = useState("");

  const goToGroup = useCallback(
    (id: string, name: string) =>
      confirmLeave(() => {
        flushSync(() => {
          setGroupLoading(true);
          setGroupLoadingName(name);
        });

        dispatch(getGroupOfStopPlacesById(id))
          .then(() => {
            dispatch(
              UserActions.navigateTo(`/${Routes.GROUP_OF_STOP_PLACE}/`, id),
            );
            // Loading stays true — this component unmounts when the group renders.
          })
          .catch(() => {
            setGroupLoading(false);
            setGroupLoadingName("");
          });
      }),
    [dispatch, confirmLeave],
  );

  return {
    loading: parentLoading || groupLoading,
    loadingName: parentLoadingName || groupLoadingName,
    goToParent,
    goToGroup,
  };
};
